import {
  Address,
  ClinicOrder,
  ClinicOrderStatus,
  CreateOrderRequest,
  CustomerType,
  GuestContact,
  OrderItem,
  OrderTimelineEvent,
  VirtualDeliveryStatus,
} from '../types';

const EMPTY_ADDRESS: Address = {
  line1: '',
  suburb: '',
  state: '',
  postcode: '',
  country: 'AU',
};

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v.trim() : undefined;

/**
 * The order address snapshot is not guaranteed to come back as the structured
 * frontend `Address` (see docs/backend-must-fix.md item 1). Normalize whatever
 * shape arrives — a structured object with alternate key names, or a single
 * formatted string — into a full `Address` so the detail page never shows a
 * half-empty address.
 */
const mapAddressSnapshot = (raw: unknown): Address | null => {
  if (!raw) return null;
  // Some backends store the snapshot as one pre-formatted line.
  if (typeof raw === 'string') {
    const s = raw.trim();
    return s ? { ...EMPTY_ADDRESS, line1: s } : null;
  }
  if (typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;
  const line1 =
    str(a.line1) ?? str(a.street) ?? str(a.address1) ?? str(a.addressLine1) ?? '';
  const suburb = str(a.suburb) ?? str(a.city) ?? str(a.locality) ?? '';
  const state = str(a.state) ?? str(a.region) ?? str(a.province) ?? '';
  const postcode =
    str(a.postcode) ?? str(a.postalCode) ?? str(a.zip) ?? str(a.zipCode) ?? '';
  // Nothing usable → treat as absent rather than render an empty card.
  if (!line1 && !suburb && !state && !postcode) return null;
  return {
    line1,
    line2: str(a.line2) ?? str(a.address2) ?? str(a.addressLine2),
    suburb,
    state,
    postcode,
    country: str(a.country) ?? 'AU',
    contactName: str(a.contactName) ?? str(a.recipient) ?? str(a.name),
    contactPhone: str(a.contactPhone) ?? str(a.phone),
  };
};

/**
 * Backend currently emits naive ISO datetimes like `2026-05-11T05:27:48.352542`
 * (no `Z`, no offset). JavaScript parses these as LOCAL time, causing timezone
 * skew on countdowns / timeline rendering. Assume UTC and patch in `Z`.
 * Backend should fix this by always emitting tz-aware ISO 8601.
 */
const toUtcIso = (raw?: string | null): string | undefined => {
  if (!raw) return undefined;
  return /[Zz]|[+-]\d{2}:\d{2}$/.test(raw) ? raw : `${raw}Z`;
};

export type OrderItemBuyerDtoBackend = {
  id?: string;
  productId: string;
  /**
   * Backend currently sends `productNameEn` / `productNameZh`. Older field
   * names (`nameEn` / `nameZh` / `productName`) are kept as fallbacks while
   * the backend stabilizes the contract.
   */
  productNameEn?: string | null;
  productNameZh?: string | null;
  productName?: string | null;
  nameEn?: string | null;
  nameZh?: string | null;
  productType?: 'PHYSICAL' | 'VIRTUAL' | null;
  category?: string | null;
  qty: number;
  unitPriceSnapshot: number;
  gstRateSnapshot: number;
  lineTotal: number;
  virtualDeliveryStatus?: string | null;
  /** Product image URL snapshot taken at order time. */
  imageUrl?: string | null;
};

export type OrderBuyerDtoBackend = {
  id: string;
  orderNumber?: string | null;
  customerType?: string | null;
  businessId?: string | null;
  businessName?: string | null;
  placedByUserId?: string | null;
  placedByUserName?: string | null;
  status: ClinicOrderStatus;
  fulfillmentMode?: string | null;
  subtotal: number;
  gst: number;
  freight: number;
  handlingFee: number;
  total: number;
  shippingAddressSnapshot?: unknown;
  billingAddressSnapshot?: unknown;
  deliveryEmail?: string | null;
  carrier?: string | null;
  cardBrand?: string | null;
  cardLast4?: string | null;
  trackingNumber?: string | null;
  shippingMethod?: string | null;
  notes?: string | null;
  createdAt: string;
  reservedUntil?: string | null;
  paidAt?: string | null;
  dispatchedAt?: string | null;
  receivedAt?: string | null;
  completedAt?: string | null;
  items?: OrderItemBuyerDtoBackend[] | null;
};

const mapOrderItem = (b: OrderItemBuyerDtoBackend): OrderItem => ({
  productId: b.productId,
  productNameEn: b.productNameEn ?? b.nameEn ?? b.productName ?? '',
  productNameZh: str(b.productNameZh) ?? str(b.nameZh),
  unit: '',
  quantity: b.qty,
  unitPrice: b.unitPriceSnapshot,
  gstRate: b.gstRateSnapshot,
  lineTotal: b.lineTotal,
  productType: b.productType ?? undefined,
  virtualDeliveryStatus: (b.virtualDeliveryStatus as VirtualDeliveryStatus) ?? undefined,
  imageUrl: str(b.imageUrl),
});

const buildTimeline = (b: OrderBuyerDtoBackend): OrderTimelineEvent[] => {
  const events: OrderTimelineEvent[] = [
    { status: 'PENDING_PAYMENT', occurredAt: toUtcIso(b.createdAt) ?? b.createdAt },
  ];
  const paidAt = toUtcIso(b.paidAt);
  const dispatchedAt = toUtcIso(b.dispatchedAt);
  const receivedAt = toUtcIso(b.receivedAt);
  const completedAt = toUtcIso(b.completedAt);
  if (paidAt) events.push({ status: 'PAID', occurredAt: paidAt });
  if (dispatchedAt) events.push({ status: 'DISPATCHED', occurredAt: dispatchedAt });
  if (receivedAt) events.push({ status: 'RECEIVED', occurredAt: receivedAt });
  if (completedAt) events.push({ status: 'COMPLETED', occurredAt: completedAt });
  return events;
};

export const mapOrderBuyerDto = (b: OrderBuyerDtoBackend): ClinicOrder => ({
  id: b.id,
  orderNumber: b.orderNumber ?? undefined,
  status: b.status,
  createdAt: toUtcIso(b.createdAt) ?? b.createdAt,
  paymentDeadline: toUtcIso(b.reservedUntil),
  items: (b.items ?? []).map(mapOrderItem),
  subtotal: b.subtotal,
  gstTotal: b.gst,
  freight: b.freight ?? 0,
  handlingFee: b.handlingFee ?? 0,
  total: b.total,
  shippingAddress: mapAddressSnapshot(b.shippingAddressSnapshot),
  billingAddress: mapAddressSnapshot(b.billingAddressSnapshot),
  deliveryEmail: b.deliveryEmail ?? undefined,
  cardBrand: b.cardBrand ?? undefined,
  cardLast4: b.cardLast4 ?? undefined,
  carrier: b.carrier ?? undefined,
  trackingNumber: b.trackingNumber ?? undefined,
  shippingMethod: str(b.shippingMethod),
  notes: str(b.notes),
  timeline: buildTimeline(b),
  customerType: (b.customerType as CustomerType) ?? undefined,
  businessId: b.businessId ?? undefined,
  businessName: b.businessName ?? undefined,
  placedByUserId: b.placedByUserId ?? undefined,
  placedByUserName: b.placedByUserName ?? undefined,
});

/** Frontend `CreateOrderRequest.shippingAddress` already matches backend AddressDto. */
export const mapCreateOrderRequest = (req: CreateOrderRequest) => ({
  shippingAddress: req.shippingAddress,
  // Always send billing so backend has a concrete address (no "same as shipping"
  // null sentinel). Checkout copies shipping into billing when the user opts in.
  billingAddress: req.billingAddress ?? req.shippingAddress,
  deliveryEmail: req.deliveryEmail,
  // The frontend signature also supports guest/items overrides for guest checkout flows.
  // For the authenticated buyer endpoint the backend reads items server-side from the cart,
  // BUT swagger declares `items` in CreateOrderRequest body. Pass it through if provided.
  items: req.guestItems?.map((g) => ({ productId: g.productId, qty: g.quantity })),
});

// ---------- Guest order lookup mapping ----------
//
// Public endpoints `/public/orders/lookup`, `/public/orders/{no}/pay` and
// `/public/orders/{no}/cancel` all return `GuestOrderLookupDto`, which has a
// different shape from the authed `OrderBuyerDto` (e.g. `qty` vs
// `unitPriceSnapshot`, no item id, no virtualDeliveryStatus, top-level
// `publicOrderNo` etc.). Keep a separate mapper so the frontend `ClinicOrder`
// shape stays stable across both paths.

export type GuestOrderItemDtoBackend = {
  productId: string;
  /** Legacy single name; kept until backend always sends nameEn/nameZh. */
  productName?: string | null;
  nameEn?: string | null;
  nameZh?: string | null;
  productType?: 'PHYSICAL' | 'VIRTUAL' | null;
  category?: string | null;
  qty: number;
  unitPrice: number;
  /** Per-item GST rate snapshot (e.g. `0.1`). */
  gstRateSnapshot?: number | null;
  /** Line total snapshot from the backend; already includes GST. */
  lineTotal?: number | null;
  /** Product image URL snapshot. May be a backend-internal host until the backend serves a public URL. */
  imageUrl?: string | null;
  virtualDeliveryStatus?: VirtualDeliveryStatus | string | null;
};

export type GuestOrderLookupDtoBackend = {
  publicOrderNo?: string | null;
  orderNumber?: string | null;
  customerType?: string | null;
  status: ClinicOrderStatus;
  fulfillmentMode?: string | null;
  reservedUntil?: string | null;
  items?: GuestOrderItemDtoBackend[] | null;
  subtotal: number;
  gst: number;
  freight: number;
  handlingFee: number;
  total: number;
  carrier?: string | null;
  trackingNumber?: string | null;
  shippingMethod?: string | null;
  notes?: string | null;
  createdAt: string;
  paidAt?: string | null;
  dispatchedAt?: string | null;
  completedAt?: string | null;
  // Buyer / customer info (top-level on the guest lookup payload).
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  // Address snapshots; `billingAddressSnapshot = null` means "same as shipping".
  shippingAddressSnapshot?: unknown;
  billingAddressSnapshot?: unknown;
};

const mapGuestItem = (b: GuestOrderItemDtoBackend): OrderItem => ({
  productId: b.productId,
  productNameEn: b.nameEn ?? b.productName ?? '',
  productNameZh: str(b.nameZh),
  unit: '',
  quantity: b.qty,
  unitPrice: b.unitPrice,
  gstRate: b.gstRateSnapshot ?? 0,
  // Prefer the backend line total (includes GST); fall back to a bare unit×qty.
  lineTotal: b.lineTotal ?? b.unitPrice * b.qty,
  productType: b.productType ?? undefined,
  imageUrl: str(b.imageUrl),
  virtualDeliveryStatus:
    (b.virtualDeliveryStatus as VirtualDeliveryStatus) ?? undefined,
});

const buildGuestTimeline = (b: GuestOrderLookupDtoBackend): OrderTimelineEvent[] => {
  const events: OrderTimelineEvent[] = [
    { status: 'PENDING_PAYMENT', occurredAt: toUtcIso(b.createdAt) ?? b.createdAt },
  ];
  const paidAt = toUtcIso(b.paidAt);
  const dispatchedAt = toUtcIso(b.dispatchedAt);
  const completedAt = toUtcIso(b.completedAt);
  if (paidAt) events.push({ status: 'PAID', occurredAt: paidAt });
  if (dispatchedAt) events.push({ status: 'DISPATCHED', occurredAt: dispatchedAt });
  if (completedAt) events.push({ status: 'COMPLETED', occurredAt: completedAt });
  return events;
};

/**
 * The guest lookup payload carries the buyer as a flat `contactName` / `email` /
 * `phone`. The detail page expects a `GuestContact` (first/last split), so adapt
 * here. `contactName` is a single full name — split on the first space.
 */
const buildGuestContact = (
  b: GuestOrderLookupDtoBackend,
  fallbackEmail: string,
): GuestContact | undefined => {
  const fullName = str(b.contactName);
  const email = str(b.email) ?? fallbackEmail;
  const phone = str(b.phone);
  if (!fullName && !phone && !str(b.email)) return undefined;
  const [firstName, ...rest] = (fullName ?? '').split(' ');
  return {
    email,
    firstName: firstName ?? '',
    lastName: rest.join(' '),
    phone,
  };
};

export const mapGuestLookupDto = (
  b: GuestOrderLookupDtoBackend,
  email: string,
): ClinicOrder => ({
  // Guest orders use publicOrderNo as the stable identifier across pay/cancel.
  id: b.publicOrderNo ?? b.orderNumber ?? '',
  orderNumber: b.orderNumber ?? b.publicOrderNo ?? undefined,
  status: b.status,
  createdAt: toUtcIso(b.createdAt) ?? b.createdAt,
  paymentDeadline: toUtcIso(b.reservedUntil),
  items: (b.items ?? []).map(mapGuestItem),
  subtotal: b.subtotal,
  gstTotal: b.gst,
  freight: b.freight ?? 0,
  handlingFee: b.handlingFee ?? 0,
  total: b.total,
  shippingAddress: mapAddressSnapshot(b.shippingAddressSnapshot),
  billingAddress: mapAddressSnapshot(b.billingAddressSnapshot),
  carrier: b.carrier ?? undefined,
  trackingNumber: b.trackingNumber ?? undefined,
  shippingMethod: str(b.shippingMethod),
  notes: str(b.notes),
  timeline: buildGuestTimeline(b),
  customerType: (b.customerType as CustomerType) ?? 'GUEST',
  guestEmail: str(b.email) ?? email,
  guestContact: buildGuestContact(b, email),
});
